import { marketMeta, marketMetaIt } from '~/search-domain/config/market-meta';
import { type SearchRepubCountry } from '~/search-domain/types';

/**
 * Query-side mirror of `utils/response-factories/isMarketSupported.ts`, which resolves a market's
 * display name as `marketDisplayNameMap[country] ?? marketDisplayNameMap.us` and keeps it only if
 * that name is a key of the country's market-meta lookup.
 *
 * Without this the markets terms aggregation is capped *before* the supported-market rule runs, so
 * heavily-priced period markets ("1st Half Point Spread") can fill the bucket allowance and push a
 * main market out — the card then renders without its main market, or is dropped entirely. Filtering
 * first makes the bucket population the supported set, so a small cap is safe.
 */

/**
 * ES stores the main Italian points market with a trailing space. `isMarketSupported` never sees it
 * because zod trims the name first; this query compares raw keyword bytes, so the untrimmed value
 * must be listed explicitly or GZ loses the market once query caps are enabled. Remove once the
 * source data is corrected. See `tests/supportedMarketsQuery.test.ts`.
 */
const UNTRIMMED_MARKET_NAMES: Partial<Record<SearchRepubCountry, string[]>> = {
  it: ['Totale Punti '],
};

export function supportedMarketsQueryFactory(repubCountry: SearchRepubCountry) {
  const supportedNames = [
    ...Object.keys(repubCountry === 'it' ? marketMetaIt : marketMeta),
    ...(UNTRIMMED_MARKET_NAMES[repubCountry] ?? []),
  ];
  const countryField = `marketDisplayNameMap.${repubCountry}.keyword`;

  // Mirrors the `?? marketDisplayNameMap.us` fallback: a document with no name for this country is
  // judged on its US name, against this country's own supported list. For `us` itself the fallback
  // clause is self-contradictory (`must_not exists('marketDisplayNameMap.us')` alongside a `terms`
  // match on that same field) and never matches, so this one shape is correct for every country —
  // see tests/supportedMarketsQuery.test.ts.
  return {
    bool: {
      minimum_should_match: 1,
      should: [
        { terms: { [countryField]: supportedNames } },
        {
          bool: {
            must: [{ terms: { 'marketDisplayNameMap.us.keyword': supportedNames } }],
            must_not: [{ exists: { field: `marketDisplayNameMap.${repubCountry}` } }],
          },
        },
      ],
    },
  };
}
